import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AfterContentInit,
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { combineLatest, fromEvent, Observable, Subscription } from 'rxjs';
import { combineLatestWith, debounceTime, filter, map, skip, startWith, switchMap } from 'rxjs/operators';
import { CATEGORY_PLACEHOLDER } from 'src/app/app.settings';
import { Category, FilterParams, isForest, Product, ProductFilter, StoreType } from 'src/app/models';
import { buttonIcons, symbolIcons } from 'src/app/modules/icon.module';
import { AuthService } from 'src/app/services/auth';
import { CategoryService } from 'src/app/services/category/category.service';
import { ContentService } from 'src/app/services/content/content.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { KioskService } from 'src/app/services/kiosk/kiosk.service';
import { ProductQuery, ProductService } from 'src/app/services/product';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { FilterMenuComponent } from 'src/app/shared/components/filter-menu/filter-menu.component';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { environment } from 'src/environments/environment';

export const setFilters = (params: Params): ProductFilter => {
  const filters: any = {};

  for (const [key, value] of Object.entries(params)) {
    const filterKey = Object.keys(filters).find((entry) => entry.toLowerCase() === key.toLowerCase());

    if (filterKey) {
      filters[filterKey] = value;
    }
  }

  return filters;
};

@Component({
  selector: 'app-product-overview-utilty',
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('showHide', [
      state(
        'show',
        style({
          transform: 'translateY(0)',
          opacity: 1,
        }),
      ),
      state(
        'hide',
        style({
          transform: 'translateY(100%)',
          opacity: 0,
        }),
      ),
      transition('show => hide', [animate('500ms ease-out')]),
      transition('hide => show', [animate('500ms ease-out')]),
    ]),
  ],
})
export class ProductOverviewUtility extends ContentSection implements OnInit, AfterContentInit, AfterViewInit, AfterViewChecked, OnDestroy {
  protected activatedRoute = inject(ActivatedRoute);
  protected router = inject(Router);
  protected productQuery = inject(ProductQuery);
  protected productService = inject(ProductService);
  protected categoryService = inject(CategoryService);
  protected authService = inject(AuthService);
  protected cdr = inject(ChangeDetectorRef);
  protected bottomSheet = inject(MatBottomSheet);
  protected themeService = inject(ThemeService);
  protected kioskService = inject(KioskService);
  protected elRef = inject(ElementRef);
  protected sanitizer = inject(DomSanitizer);
  protected dialogService = inject(DialogService);

  private subscription = new Subscription();
  cmsOrigin = environment.cmsOrigin;
  placeholder = CATEGORY_PLACEHOLDER;
  clearIcon = buttonIcons.bigCross;
  arrowIcon = symbolIcons.left_arrow;

  @HostBinding('style.background-image') backgroundStyle: SafeStyle;
  @HostBinding('class.kiosk') isKiosk: boolean;

  products$: Observable<Product[]>;
  loading$: Observable<boolean>;
  filters: ProductFilter;
  category$: Observable<Category>;

  categories$: Observable<Category[]>;
  pageTotal$: Observable<number>;

  page$: Observable<number>;
  pageSize$: Observable<number>;
  pageShowing$: Observable<number>;

  filterMenu: { categoryTitle: string; allProducts: string };
  loadingProductsAlt: string;
  openCategoriesMenu: string;
  loadMoreButton: string;
  allProductsTitle: string;

  routeLoading = false;
  reset = false;

  isDagrofa: boolean;
  showBasketIcon: boolean;

  showScrollToTop: boolean;
  @ViewChild('breadcrumbRef', { read: ElementRef, static: false }) breadcrumbRef!: ElementRef;

  enableSearch: boolean;
  searchTerm = '';
  searchPlaceholder: string;
  @ViewChild('searchInput') searchInput: ElementRef;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, 'productsOverview');

    this.isDagrofa = this.contentService.isDagrofa;
    this.isKiosk = this.kioskService.isKiosk;

    const type = this.contentService.getMerchant().type;
    this.showBasketIcon = type === StoreType.SPAR;

    this.filterMenu = {
      categoryTitle: this.content.get('categoryLabelText'),
      allProducts: this.content.get('allProductsFilterText') || 'Se alle',
    };

    this.loadingProductsAlt = this.content.get('loadingProductsAlternativeText');
    this.openCategoriesMenu = this.content.get('openCategoriesMenuText');
    this.loadMoreButton = this.content.get('loadMoreButtonText') || 'Vis flere';
    this.allProductsTitle = this.content.get('allProductsLabelText');
    this.searchPlaceholder = this.content.get('searchPlaceholderText') || 'Hvad søger du?'; // TODO: make umbraco content

    this.filters = {};

    this.productService.updateSearchOverview(true);

    this.category$ = this.categoryService.currentCategory.pipe(
      map((categories) => {
        if (isForest(categories)) {
          this.contentService.setOGImage();
          this.setMeta();
          return undefined;
        }

        const category = categories.data;
        return category;
      }),
    );

    this.subscription.add(
      combineLatest([this.router.events.pipe(filter((e) => e instanceof NavigationEnd)), this.activatedRoute.queryParams, this.category$])
        .pipe(debounceTime(1))
        .subscribe(([url, params, category]) => {
          this.reset = true;
          if (category) this.clearSearch();
          this.filters = setFilters(params);
          this.fetchProducts(true);
        }),
    );

    this.subscription.add(
      this.categoryService.currentCategory.asObservable().subscribe(() => {
        this.reset = true;
        this.fetchProducts(true);
      }),
    );

    this.subscription.add(
      this.category$.subscribe((category) => {
        if (!category?.backgroundImageUrl) {
          this.backgroundStyle = '';
          return;
        }
        let imageStyle = `url(${this.cmsOrigin}${category.backgroundImageUrl})`;
        if (category.backgroundImageDarken) imageStyle = `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), ${imageStyle}`;
        this.backgroundStyle = this.sanitizer.bypassSecurityTrustStyle(imageStyle);
      }),
    );
  }

  ngOnInit() {
    const merchant = this.contentService.getMerchant();
    this.enableSearch = merchant.enableSearch;
    this.products$ = this.productQuery.selectSearchResults$.pipe(switchMap((ids) => this.productQuery.selectMany(ids)));
    this.pageTotal$ = this.productQuery.pagedResponse$.pipe(map((page) => page.total));
    this.loading$ = this.productQuery.selectLoading();
    if (merchant.enableCategoryCards) this.categories$ = this.categoryService.getCurrentDisplayCategories();

    this.pageSize$ = this.productQuery.selectPageSize$;
    this.pageShowing$ = this.productQuery.selectPage$.pipe(
      combineLatestWith(this.pageSize$),
      map(([page, pageSize]) => page * pageSize),
    );
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngAfterViewInit() {
    if (typeof window !== 'undefined') {
      const toggleStickyRef = this.toggleSticky.bind(this);
      this.subscription.add(fromEvent(window, 'scroll').pipe().subscribe(toggleStickyRef));
    }

    if (!this.enableSearch) return;
    this.searchTerm = this.productQuery.getSearchTerm();
    const searchTerms = fromEvent<{ target: { value: string } }>(this.searchInput.nativeElement, 'input').pipe(
      map((event) => event.target.value),
      startWith(this.searchTerm),
      skip(1),
      debounceTime(200),
    );

    this.subscription.add(
      searchTerms.subscribe((searchTerm) => {
        const updated = this.productService.updateSearchTerm(searchTerm);
        if (updated) {
          this.fetchProducts(true);
        }
      }),
    );
  }

  ngAfterViewChecked() {
    if (this.reset) {
      this.reset = false;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    this.productService.updateSearchFilters({});
    this.productService.updateSearchTerm('');
    this.productService.updateSearchOverview(false);
    this.contentService.setOGImage();
    this.subscription.unsubscribe();
  }

  updateFilters(filters: FilterParams<Product>) {
    const filterParams: any = {};
    for (const [key, value] of Object.entries(filters)) {
      if (value === 'true') {
        filterParams[key] = true;
      }
    }

    this.router.navigate([], {
      queryParams: filterParams,
      queryParamsHandling: 'merge',
    });
  }

  toggleSticky() {
    this.showScrollToTop = this.isElementAboveViewport(this.breadcrumbRef.nativeElement);
  }

  isElementAboveViewport(el: HTMLElement) {
    const rect = el.getBoundingClientRect();
    return rect.bottom < 0;
  }

  hasMore() {
    return this.productQuery.getHasMore();
  }

  loadMore() {
    if (this.productQuery.getHasMore()) {
      this.fetchProducts(false);
    }
  }

  openFilterMenu() {
    if (!this.isKiosk) this.bottomSheet.open(FilterMenuComponent, { data: this.filterMenu });
    if (this.isKiosk) this.dialogService.openFilterMenuDialog(this.filterMenu);
  }

  scroll(el: HTMLElement, e?: Event) {
    e?.preventDefault();
    el.scrollIntoView({ behavior: 'smooth' });
  }

  getCategoryUrl(categoryId?: number): string {
    return this.categoryService.buildUrl(categoryId);
  }

  clickCategory(e?: Event) {
    e?.preventDefault();
  }

  searchProducts(searchTerm: string) {
    const updated = this.productService.updateSearchTerm(searchTerm);
    if (updated) {
      this.fetchProducts(true);
    }
  }

  clearSearch(fetch = false) {
    this.searchTerm = '';
    const updated = this.productService.updateSearchTerm('');
    if (fetch && updated) this.fetchProducts(true);
  }

  private async fetchProducts(reset: boolean): Promise<void> {
    const productFilter: any = {};

    for (const [key, value] of Object.entries(this.filters)) {
      if (value) {
        productFilter[key] = value.toString();
      }
    }

    this.productService.updateSearchFilters(productFilter);
    await this.productService.searchProducts(reset);
  }
}
